import { useCallback, useRef, useState } from "react";

/**
 * An action that survives a bad connection (task 9.20).
 *
 * WHY THIS EXISTS
 * Instructors use this at an airfield, on a phone, on whatever signal the
 * clubhouse has. The default behaviour of a fetch on a flaky link is to hang
 * and then fail silently, which produces the worst possible outcome: the
 * instructor taps Accept, sees nothing happen, taps again, and either
 * double-accepts or gives up and the request expires.
 *
 * Three things fix that, and they have to be together.
 *
 * OPTIMISTIC — the UI moves immediately, so a two-second round trip does not
 * look like a dead button. Rolled back if the call genuinely fails.
 *
 * RETRIED — a network error is retried with backoff, because on a marginal
 * link the second attempt usually works and asking a person to retry a thing
 * the computer could retry is just moving the work.
 *
 * IDEMPOTENT AT THE CALL SITE — double-taps are swallowed while a call is in
 * flight. That is not a nicety: without it, an instructor tapping twice on a
 * slow link sends two accepts.
 *
 * A 4xx is NOT retried. "Already accepted" or "not yours" will not become true
 * on a second attempt, and retrying makes a clear error look like a hang.
 */
const RETRYABLE = (err) => {
  const status = err?.response?.status ?? err?.status;
  // No status at all means the request never reached anyone — the flaky-link
  // case, and exactly what should be retried.
  if (status === undefined || status === null) return true;
  return status >= 500 || status === 429;
};

export default function useResilientAction({ maxAttempts = 3, baseDelayMs = 700 } = {}) {
  const [pending, setPending] = useState({});
  const [error, setError] = useState(null);
  const inFlight = useRef(new Set());

  const run = useCallback(async (key, fn, { optimistic, rollback } = {}) => {
    // Swallow the double tap rather than sending it.
    if (inFlight.current.has(key)) return { ok: false, reason: "in_flight" };
    inFlight.current.add(key);
    setPending((p) => ({ ...p, [key]: true }));
    setError(null);
    optimistic?.();

    let lastErr = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        const res = await fn();
        inFlight.current.delete(key);
        setPending((p) => ({ ...p, [key]: false }));
        return { ok: true, res };
      } catch (err) {
        lastErr = err;
        if (!RETRYABLE(err) || attempt === maxAttempts) break;
        // Backoff, so a struggling link is not hammered.
        await new Promise((r) => setTimeout(r, baseDelayMs * attempt));
      }
    }

    rollback?.();
    inFlight.current.delete(key);
    setPending((p) => ({ ...p, [key]: false }));
    // Said plainly. "Something went wrong" tells someone standing on a windy
    // apron nothing about whether to try again.
    setError(
      RETRYABLE(lastErr)
        ? "Couldn't reach XAir — check your signal and try again. Nothing was changed."
        : lastErr?.response?.data?.message || lastErr?.message || "That didn't work."
    );
    return { ok: false, error: lastErr };
  }, [maxAttempts, baseDelayMs]);

  return { run, pending, error, clearError: () => setError(null) };
}
